import { Component, OnInit, ChangeDetectionStrategy, input, output, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LTFilterRowComponent } from '../lt-filter-row/lt-filter-row.component';
import { LTFilterRule, LTFilterLogic, LTFilterColumn, LTFilter } from '../../models/lt-filter.models';
import { LTFilterService } from '../../services/lt-filter.service';

interface LTFilterRowData {
  id: string;
  filter?: LTFilterRule;
  logic: LTFilterLogic;
}

@Component({
  selector: 'app-lt-filter',
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatDividerModule,
    MatSelectModule,
    MatFormFieldModule,
    LTFilterRowComponent,
  ],
  templateUrl: './lt-filter.component.html',
  styleUrl: './lt-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LTFilterService],
})
export class LTFilterComponent implements OnInit {
  private readonly ltFilterService = inject(LTFilterService);
  
  //#region Inputs
  readonly columns = input.required<LTFilterColumn[]>();
  readonly filter = input<LTFilter>();
  readonly disabled = input(false);
  readonly showHeader = input(true);
  readonly showActions = input(true);
  //#endregion Inputs

  //#region Outputs
  readonly filterChange = output<LTFilter | null>();
  readonly apply = output<LTFilter | null>();
  readonly filterCancel = output<void>();
  //#endregion Outputs

  //#region Internal state
  readonly filterRows = signal<LTFilterRowData[]>([]);
  readonly rootLogic = signal<LTFilterLogic>('and');
  private idCounter = 0;
  //#endregion Internal state

  //#region Computed properties
  readonly hasValidFilters = computed(() => {
    return this.filterRows().some(row => row.filter != null);
  });

  readonly currentFilter = computed((): LTFilter | null => {
    const rows = this.filterRows();
    const validFilters = rows
      .map(row => row.filter)
      .filter((filter): filter is LTFilterRule => filter != null);

    if (validFilters.length === 0) return null;
    if (validFilters.length === 1) return validFilters[0];

    return this.ltFilterService.createFilterGroup(this.rootLogic(), validFilters);
  });
  //#endregion Computed properties

  ngOnInit(): void {
    this.initializeFromFilter();
  }

  addFilter(): void {
    const newRow: LTFilterRowData = {
      id: `lt-filter-${++this.idCounter}`,
      logic: this.rootLogic(),
    };
    
    this.filterRows.update(rows => [...rows, newRow]);
  }

  removeFilter(id: string): void {
    this.filterRows.update(rows => rows.filter(row => row.id !== id));
    this.emitChange();
  }

  updateFilter(id: string, filter: LTFilterRule): void {
    this.filterRows.update(rows => 
      rows.map(row => 
        row.id === id ? { ...row, filter } : row,
      ),
    );
    this.emitChange();
  }

  updateLogic(id: string, logic: LTFilterLogic): void {
    this.filterRows.update(rows => 
      rows.map(row => 
        row.id === id ? { ...row, logic } : row,
      ),
    );
    this.emitChange();
  }

  setRootLogic(logic: LTFilterLogic): void {
    this.rootLogic.set(logic);
    this.emitChange();
  }

  clearAll(): void {
    this.filterRows.set([]);
    this.emitChange();
  }

  applyFilter(): void {
    const filter = this.currentFilter();
    this.apply.emit(filter);
  }

  cancelFilter(): void {
    this.filterCancel.emit();
  }

  private initializeFromFilter(): void {
    const inputFilter = this.filter();
    if (!inputFilter) {
      // Start with one empty filter row
      this.addFilter();
      return;
    }

    if (this.ltFilterService.isFilterGroup(inputFilter)) {
      this.rootLogic.set(inputFilter.logic);
      const rows: LTFilterRowData[] = inputFilter.filters.map((filter, index) => ({
        id: `lt-filter-${++this.idCounter}`,
        filter: this.ltFilterService.isFilterRule(filter) ? filter : undefined,
        logic: inputFilter.logic,
      }));
      this.filterRows.set(rows);
    } else if (this.ltFilterService.isFilterRule(inputFilter)) {
      const row: LTFilterRowData = {
        id: `lt-filter-${++this.idCounter}`,
        filter: inputFilter,
        logic: 'and',
      };
      this.filterRows.set([row]);
    }
  }

  private emitChange(): void {
    const filter = this.currentFilter();
    this.filterChange.emit(filter);
  }
}
